/**
 * How sharply the combat board rasterizes (`projects/crisp-zoom.md`, `decisions/0187`). Three
 * pieces, kept out of `pixiCombatBoard.ts` so they test headless:
 *
 * - {@link boardResolution}: the renderer's resolution, the device pixel ratio capped at
 *   {@link MAX_BOARD_RESOLUTION}. Every vector layer is drawn live each frame, so this alone makes
 *   them sharp at every zoom.
 * - {@link textResolution}: a `Text` is rasterized once to a bitmap and then magnified by the world
 *   zoom, so it needs its own resolution, picked from a few power-of-two levels so a label
 *   re-rasterizes at most twice across the zoom range.
 * - {@link createViewSettled}: a trailing debounce over the view, so that re-rasterization happens
 *   once the zoom stops moving instead of on every wheel step.
 */
import { Text, type Container } from "pixi.js";

/** A 3× phone renders at 2×: slightly soft, and 2.25× less fill than native. */
export const MAX_BOARD_RESOLUTION = 2;
/** The highest text level; board text never rasterizes above `dpr × MAX_TEXT_LEVEL`. */
export const MAX_TEXT_LEVEL = 4;
/** How long the view must hold still before {@link createViewSettled} fires. */
export const VIEW_SETTLE_MS = 150;

/** The renderer resolution for a device pixel ratio. */
export function boardResolution(devicePixelRatio: number): number {
  return Math.min(MAX_BOARD_RESOLUTION, devicePixelRatio > 0 ? devicePixelRatio : 1);
}

/** The power of two at or above `scale`, clamped to `1..MAX_TEXT_LEVEL`: scales ≤1, (1,2] and
 *  (2,4] give 1, 2 and 4. */
export function textLevel(scale: number): number {
  if (!(scale > 1)) return 1;
  return Math.min(MAX_TEXT_LEVEL, 2 ** Math.ceil(Math.log2(scale)));
}

/** The `Text.resolution` for board text at a settled `scale` on a renderer at `resolution`. */
export function textResolution(scale: number, resolution: number): number {
  return resolution * textLevel(scale);
}

/**
 * Set `resolution` on every `Text` under `root`, skipping the `skip` subtree. Only a text whose
 * value differs is touched, since Pixi re-rasterizes a text on its next render whenever its
 * resolution changes. Returns how many texts it saw and how many it changed.
 */
export function applyTextResolution(root: Container, resolution: number, skip?: Container): { count: number; changed: number } {
  let count = 0;
  let changed = 0;
  const walk = (node: Container): void => {
    if (node === skip) return;
    if (node instanceof Text) {
      count++;
      if (node.resolution !== resolution) {
        node.resolution = resolution;
        changed++;
      }
    }
    for (const child of node.children) walk(child);
  };
  walk(root);
  return { count, changed };
}

/**
 * The view once it has stopped changing. `x`/`y`/`scale` are `setView`'s world transform; `dpr` is
 * the renderer resolution ({@link boardResolution}), so a rasterizing layer wants
 * `scale × dpr` device pixels per world pixel.
 */
export type SettledView = { x: number; y: number; scale: number; dpr: number };

export type ViewSettled = {
  /** Record a view change. Listeners fire {@link VIEW_SETTLE_MS} after the last one. */
  note(view: SettledView): void;
  /** Fire now with `view`, cancelling any pending fire. For a change that should not wait, such
   *  as a device pixel ratio change. */
  flush(view: SettledView): void;
  /** Call `listener` on every settle; returns the unsubscribe. */
  subscribe(listener: (view: SettledView) => void): () => void;
  /** The last settled view, or `null` before the first settle. */
  last(): SettledView | null;
  /** Cancel any pending fire and drop every listener. */
  dispose(): void;
};

export type Timers = {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (id: unknown) => void;
};

const globalTimers: Timers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
};

/** A trailing debounce over the board view. `timers` is injectable for tests. */
export function createViewSettled(opts: { delayMs?: number; timers?: Timers } = {}): ViewSettled {
  const delayMs = opts.delayMs ?? VIEW_SETTLE_MS;
  const timers = opts.timers ?? globalTimers;
  const listeners = new Set<(view: SettledView) => void>();
  let pending: unknown = null;
  let latest: SettledView | null = null;
  let settled: SettledView | null = null;

  const cancel = (): void => {
    if (pending !== null) timers.clearTimeout(pending);
    pending = null;
  };
  const fire = (view: SettledView): void => {
    cancel();
    settled = view;
    for (const listener of [...listeners]) listener(view);
  };

  return {
    note(view) {
      latest = view;
      cancel();
      pending = timers.setTimeout(() => {
        pending = null;
        if (latest) fire(latest);
      }, delayMs);
    },
    flush(view) {
      latest = view;
      fire(view);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    last: () => settled,
    dispose() {
      cancel();
      listeners.clear();
    },
  };
}
